import axios from "axios";

/**
 * Same-origin axios client.
 *
 * Cookies:
 *  - eyvo_auth    HttpOnly access JWT (15 min — Session 48 rotation).
 *  - eyvo_refresh HttpOnly refresh JWT (7 days, path=/api/auth) used by
 *                 the interceptor below to silently mint a new access
 *                 cookie when one has naturally expired.
 *  - eyvo_csrf    Readable double-submit token mirrored into the
 *                 X-CSRF-Token header on mutating requests.
 *
 * The browser holds the tokens; JS never reads them directly.
 * `withCredentials: true` makes axios send & receive the cookies.
 *
 * Reliability contract (2026-10-21 Sales Leads freeze):
 *  - EVERY request has a finite timeout (no infinite spinners). Long
 *    operations (uploads, downloads, PDF/LLM/export paths) get a longer,
 *    still finite budget; callers may pass an explicit `timeout`.
 *  - Token refresh is single-flight across the tab AND across tabs
 *    (Web Locks), bounded by a timeout, and attempted at most once per
 *    request chain. A failed refresh redirects to login exactly once —
 *    no 401 → refresh → 401 storms.
 *  - 403 is a permission answer, never treated as an expired session.
 */
const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const normalizedBackendUrl = rawBackendUrl.replace(/\/+$/, "");
const API_BASE_URL = normalizedBackendUrl ? `${normalizedBackendUrl}/api` : "/api";

export const DEFAULT_TIMEOUT_MS = 45000;
export const LONG_TIMEOUT_MS = 180000;
const REFRESH_TIMEOUT_MS = 20000;
// Paths whose server work is legitimately slow (documents, LLM, bulk I/O).
const LONG_PATHS = /\/(pdf|preview|export|import|excel|chat|data-chat|brochure|transcri|upload|docusign|backups\/run|send)(\/|\?|$)/i;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
});

function readCookie(name) {
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name.replace(/[-.]/g, "\\$&") + "=([^;]+)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const MUTATING = new Set(["post", "put", "patch", "delete"]);

export function timeoutFor(config) {
  const isLong =
    (typeof FormData !== "undefined" && config.data instanceof FormData) ||
    config.responseType === "blob" ||
    LONG_PATHS.test(config.url || "");
  return isLong ? LONG_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (MUTATING.has(method)) {
    const csrf = readCookie("eyvo_csrf");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  // The instance default applies when the caller passed nothing; raise
  // it for known-long work. An explicit caller timeout always wins.
  if (!config.timeout || config.timeout === DEFAULT_TIMEOUT_MS) {
    config.timeout = timeoutFor(config);
  }
  config._startedAt = config._startedAt || Date.now();
  return config;
});

// ── Silent refresh on 401 ──────────────────────────────────────────────────
// When an access cookie has expired mid-session, the backend returns 401.
// We refresh ONCE and retry the original request.
//
// The server burns the whole token family if an already-rotated refresh
// token is presented (reuse detection). Two tabs refreshing at the same
// moment, or a request that started before another refresh rotated the
// cookie, used to trip that and log the operator out (prod log,
// 2026-10-21 15:15: a burst of refresh → 401). So:
//   * refreshes are serialised across tabs with the Web Locks API;
//   * the last successful refresh time is shared via localStorage, and a
//     request that started BEFORE it simply retries with the new cookie
//     instead of refreshing again.

const LAST_REFRESH_KEY = "eyvo_last_refresh_at";
const REFRESH_LOCK = "eyvo-auth-refresh";
let refreshInFlight = null;
let lastRefreshAt = 0;
let sessionDead = false;

function readLastRefresh() {
  try {
    return Math.max(lastRefreshAt, Number(localStorage.getItem(LAST_REFRESH_KEY)) || 0);
  } catch {
    return lastRefreshAt;
  }
}

function markRefreshed() {
  lastRefreshAt = Date.now();
  try { localStorage.setItem(LAST_REFRESH_KEY, String(lastRefreshAt)); } catch { /* private mode */ }
}

function isAuthRefreshPath(url) {
  // Don't try to refresh when the refresh endpoint or login endpoints
  // themselves 401. Guarding against infinite loops.
  if (!url) return false;
  return (
    url.includes("/auth/refresh") ||
    url.includes("/auth/login") ||
    url.includes("/auth/google") ||
    url.includes("/auth/verify-totp") ||
    url.includes("/auth/verify-recovery-code") ||
    url.includes("/auth/logout")
  );
}

async function doRefresh(requestStartedAt) {
  // Another tab (or an earlier chain in this tab) already rotated the
  // cookie after this request was sent — just retry with it.
  if (readLastRefresh() > requestStartedAt) return;
  await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
    withCredentials: true,
    timeout: REFRESH_TIMEOUT_MS,
  });
  markRefreshed();
}

export function performSilentRefresh(requestStartedAt = 0) {
  if (!refreshInFlight) {
    const run = () => doRefresh(requestStartedAt);
    const locked =
      typeof navigator !== "undefined" && navigator.locks?.request
        ? navigator.locks.request(REFRESH_LOCK, run)
        : run();
    refreshInFlight = Promise.resolve(locked).finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

function redirectToLogin() {
  // Pages that are intentionally reachable without auth — don't bounce
  // the viewer back to the landing page if they're already on one.
  const PUBLIC_PATHS = ["/guide/client", "/guide/internal", "/login", "/signup"];
  const here = window.location.pathname;
  const onPublicPage = PUBLIC_PATHS.some((p) => here === p || here.startsWith(p + "/"));
  if (here !== "/" && !onPublicPage) {
    // Session 69 — preserve the deep-link path through the login flow
    // as `?next=` so LoginPage can route them back after auth.
    const target = window.location.pathname + (window.location.search || "");
    window.location.href = `/?next=${encodeURIComponent(target)}`;
  }
}

api.interceptors.response.use(
  (response) => {
    sessionDead = false;
    return response;
  },
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";

    if (status === 401 && !original._retried && !isAuthRefreshPath(url) && !sessionDead) {
      original._retried = true;
      try {
        await performSilentRefresh(original._startedAt || 0);
        // Re-mirror the (possibly fresh) CSRF token and retry.
        if (MUTATING.has((original.method || "get").toLowerCase())) {
          const csrf = readCookie("eyvo_csrf");
          if (csrf) {
            original.headers = { ...(original.headers || {}), "X-CSRF-Token": csrf };
          }
        }
        original._startedAt = Date.now();
        return api(original);
      } catch (_) {
        // Refresh failed — the session is truly dead. Every other request
        // that 401s now fails fast instead of starting another refresh.
        sessionDead = true;
      }
    }

    if (status === 401) {
      // Don't redirect when the failing call IS the AuthContext bootstrap
      // probe (`/auth/me`) or any auth-helper path — those return 401 by
      // design for unauthenticated viewers.
      if (!(isAuthRefreshPath(url) || url.includes("/auth/me"))) {
        redirectToLogin();
      }
    }
    return Promise.reject(error);
  }
);

/** True when a request was deliberately cancelled (route change/unmount). */
export function isCancelled(err) {
  return axios.isCancel(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
}

/** True when a request hit its client-side timeout. */
export function isTimeout(err) {
  return err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT" ||
    /timeout/i.test(err?.message || "");
}

/** Short support reference for an error message (best effort). */
export function referenceIdFor(err, prefix = "API") {
  const fromServer =
    err?.response?.headers?.["x-request-id"] ||
    err?.response?.data?.referenceId ||
    err?.response?.data?.reference_id;
  if (fromServer) return String(fromServer);
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const rand = Math.random().toString(16).slice(2, 6).toUpperCase();
  return `${prefix}-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-` +
    `${pad(d.getHours())}${pad(d.getMinutes())}-${rand}`;
}

/** Plain-English description of what went wrong with a request. */
export function describeApiError(err) {
  if (isTimeout(err)) return "The server took too long to respond.";
  if (!err?.response) return "The server could not be reached. Check your connection.";
  const s = err.response.status;
  if (s === 401) return "Your session has expired. Please sign in again.";
  if (s === 403) return "You don't have permission to view this.";
  if (s === 404) return "The requested data was not found.";
  if (s >= 500) return "The server encountered an error.";
  const detail = err.response.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return `Request failed (HTTP ${s}).`;
}

export default api;
